import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import {
  Button,
  DatePicker,
  Form,
  Input,
  Modal,
  Radio,
  Select,
  Spin,
  Upload } from 'antd';
import { withTranslation } from 'react-i18next';

import * as ArrayUtils from '@utils/array';
import * as OwnersActions from '@actions/owners';
import {
  UploadOutlined,
} from '@ant-design/icons';


const RESET_STATE = (state = {}) => {
  return {
    isFetchingOwners: false,
    ownersById: {},
    errorfetchingOwners: null,
    createNewOwner: false,
    createNewOwnerIsCompany: null,
    fileList: [],
    ...state,
  }
}

class AddOwnerForm extends React.Component {
  constructor(props) {
    super(props);
    this.state = RESET_STATE();
    this.onOk = this.onOk.bind(this);
    this.fetchOwners = this.fetchOwners.bind(this);
  }

  componentDidMount() {
    this.fetchOwners();
  }

  componentDidUpdate(prevProps) {
    if ((prevProps.visible !== this.props.visible) && this.props.visible) {
      this.setState(RESET_STATE());
      this.fetchOwners();
    }
  }

  async fetchOwners(params = {}) {
    this.setState({ isFetchingOwners: true })
    try {
      let responseData = await this.props.fetchOwners({
        ...params,
      });
      this.setState({
        isFetchingOwners: false,
        errorfetchingOwners: null,
        ownersById: {
          ...ArrayUtils.arrayToObject(responseData.owners),
        },
      });
      return responseData;
    } catch (error) {
      this.setState({
        isFetchingOwners: false,
        errorfetchingOwners: error,
      });
      return
    }
  }

  onOk() {
    this.props.onOk();
  }

  renderAddFromPortfolioForm() {
    const formItemLayout = {
      labelCol: { span: 6 },
      wrapperCol: { span: 17, offset: 1 },
    };

    return (
      <Form form={this.props.form} layout="horizontal">
        <Form.Item
          label="Owner"
          name="owner_id"
          rules={[{ required: true, message: "Please select owner!" }]}
          style={{ margin: 0, paddingBottom: 14 }}
          {...formItemLayout}
        >
          <Select
            style={{ width: "100%" }}
            placeholder="Please select owner"
            loading={ this.state.isFetchingOwners }
            notFoundContent={
              this.state.isFetchingOwners ? <Spin size="small" /> : null
            }
          >
            {
              Object.values(this.state.ownersById).map((owner) => (
                <Select.Option
                  key={ owner.id.toString() }
                  disabled={ this.props.ownershipOwnersIds.includes(owner.id) }
                >
                  { owner.name }
                </Select.Option>
              ))
            }
          </Select>
        </Form.Item>
      </Form>
    )
  }

  renderCreateNewOwnerForm() {
    const formItemLayout = {
      labelCol: { span: 9 },
      wrapperCol: { span: 14, offset: 1 },
    };

    const items = [
      {
        label: "Name",
        name: "name",
        rules: [{ required: true, message: "Please input official owner name!" }],
        shouldRender: true,
        render: () => <Input placeholder="Name" />,
      }, {
        label: "Owner type",
        name: "is_company",
        rules: [{ required: true, message: "Please select owner type!" }],
        shouldRender: true,
        render: () => <Radio.Group
            onChange={ (e) => this.setState({ createNewOwnerIsCompany: e.target.value }) }
          >
            <Radio key="false" value="false">
              Individual
            </Radio>
            <Radio key="true" value="true">
              Company
            </Radio>
          </Radio.Group>,
      }, {
        label: "BR Number",
        name: "br_cert_number",
        rules: [{ required: false }],
        shouldRender: (this.state.createNewOwnerIsCompany === "true"),
        render: () => <Input placeholder="BR Number"/>,
      }, {
        label: "BR Expiry Date",
        name: "br_expiry_date",
        rules: [{ required: false }],
        shouldRender: (this.state.createNewOwnerIsCompany === "true"),
        render: () => <DatePicker format={"YYYY/MM/DD"} />,
      }, {
        label: "ID Number",
        name: "id_card_number",
        rules: [{ required: false }],
        shouldRender: (this.state.createNewOwnerIsCompany === "false"),
        render: () => <Input placeholder="ID Number"/>,
      }, {
        label: "Correspondence Address",
        name: "correspondence_address",
        rules: [{ required: true, message: "Please input correspondence address!" }],
        shouldRender: true,
        render: () => <Input.TextArea rows={ 4 } placeholder="Correspondence Address" />,
      }, {
        label: "Contact Details",
        name: "contact_details",
        rules: [{ required: true, message: "Please input contact details!" }],
        shouldRender: true,
        render: () => <Input.TextArea rows={ 4 } placeholder="Contact Details" />,
      }, {
        label: "Business Registration",
        name: "br_file",
        rules: [{ required: false }],
        shouldRender: (this.state.createNewOwnerIsCompany === "true"),
        render: () => (
          <div className="dropbox">
            <Upload
                multiple={ false }
                onRemove={
                  (file) => {
                    this.setState({ fileList: [] });
                  }
                }
                beforeUpload={
                  (file) => {
                    this.setState(state => ({
                      fileList: [file],
                    }));
                    return false;
                  }
                }
                fileList={ this.state.fileList }
                name="br_file"
              >
                <Button>
                  <UploadOutlined/> { this.props.t("actions.ClickToUpload") }
                </Button>
              </Upload>
          </div>
        ),
      },
    ]

    return (
      <Form form={this.props.form} layout="horizontal">
        {
          items.filter(({ shouldRender }) => shouldRender).map((item, index) => (
            <Form.Item
              key={ index.toString() }
              label={ item.label }
              name={ item.name }
              rules={ item.rules }
              style={{ margin: 0, paddingBottom: 14 }}
              {...formItemLayout}
            >
              { item.render() }
            </Form.Item>
          ))
        }
      </Form>
    )
  }

  renderForm() {
    return this.state.createNewOwner ? this.renderCreateNewOwnerForm() : this.renderAddFromPortfolioForm();
  }

  render() {
    return (
      <Modal
        open={ this.props.visible }
        title="Add Registered Owner"
        okText={ this.props.t("actions.Submit") }
        onCancel={ this.props.onCancel }
        onOk={ this.onOk }
        confirmLoading={ this.props.confirmLoading }
      >
        { this.renderForm() }
        {
          this.state.createNewOwner ? <a onClick={ () => this.setState({ createNewOwner: false }) }>Go back</a> : <span>Don't see the owner? <a onClick={ () => this.setState({ createNewOwner: true }) }>Click Here</a></span>
        }
      </Modal>
    )
  }
}

const mapStateToProps = (state, props) => {
  return {};
}

const mapDispatchToProps = (dispatch, props) => {
  return {
    fetchOwners: (params) => {
      return (dispatch(OwnersActions.fetchOwners(params)));
    },
  };
}

const ConnectedAddOwnerForm = connect(mapStateToProps, mapDispatchToProps)(AddOwnerForm);

const WrappedAddOwnerForm = (props) => {
  const [form] = Form.useForm();
  const { formRef, ...restProps } = props;
  if (formRef) formRef(form);
  return <ConnectedAddOwnerForm {...restProps} form={form} />;
};
export default withTranslation('common')(WrappedAddOwnerForm);
